import {useEffect, useMemo, useState} from "react";
import {IUTSVVersion, parseUTSVQueryResponse, utsvOpMetricQueryReturn} from "../../../pages/workflows/operational/utils/operational-metric-helpers";
import {VersionType} from "../../../__generated__/generated_types";
import {Property} from "../../../contexts/properties/logic";
import {round} from "../../../utils/math";


// TODO @bowman - Feels big, like maybe the inputs should be an object or all under a useReducer instead of separate state vars?
export interface IUseRevenueWizardState {
    property: Property;
    budgetYear: number;
    unitTypeData: IUnitType[];
    rfcstExpirations: number[] | null;
    bdgtExpirations: number[] | null;
    correctedBdgtExpirations: Map<string, number[]>;
    occupancyRate: number;
    renewalRate: number;
    forceRounding: boolean;
    isFormDirty: boolean;
    selectedMetricToFavor: TMetricFavorable;
    setSelectedMetricToFavor: (metric: TMetricFavorable) => void;
    setRfcstExpirations: (expirations: number[] | null) => void;
    setBdgtExpirations: (expirations: number[] | null) => void;
    setCorrectedBdgtExpirations: (expirations: Map<string, number[]>) => void;
    setOccupancyRate: (rate: number) => void;
    setRenewalRate: (rate: number) => void;
    setForceRounding: (round: boolean) => void;
    getValueWithRounding: (value: number) => number;
    countToRate: (count: number, total: number) => number;
    rateToCount: (rate: number, total: number) => number;
    initialize: () => void;
    resetData: () => void;
    getCorrectedValuesFavorRenewals: (unitType: IUnitType, monthIndex: number, versionType: VersionType) => {renewalRate: number, occupancyRate: number, moveInCount: number};
    getCorrectedValuesFavorOccupancy: (unitType: IUnitType, monthIndex: number, versionType: VersionType) => {renewalRate: number, occupancyRate: number, moveInCount: number};
    convertValuesWithFavor: (unitTypes: IUnitType[], metric: TMetricFavorable) => IUnitType[];
    getUnitTypeVersionSummary: (unitTypes: IUnitType[]) => IUnitType;
    getPreviousOccupancy: (unitType: IUnitType, versionType: VersionType, monthIndex: number) => number;
}

export type TMetricFavorable = "renewals" | "occupancy";

export interface IUnitTypeMetrics {
    expirationCount: number[];
    occupancyCount: number[];
    renewalCount: number[];
    moveOutCount: number[];
    moveInCount: number[];
}

// actualsOccupancyCount is the number of occupied units from the last month of the most recent actuals data
// its needed when calculating moveIns for cases where the property rfcst start month is 0
export interface IUnitType {
    name: string;
    unitCount: number;
    actualsOccupancyCount: number;
    rfcst: IUnitTypeMetrics;
    bdgt: IUnitTypeMetrics;
}

const DEFAULTS = {
    rfcstExpirations: null,
    bdgtExpirations: null,
    correctedBdgtExpirations: new Map(),
    renewalRate: 60,
    occupancyRate: 90,
    forceRounding: false,
    selectedMetricToFavor: "renewals",
    parsedMetricData: [],
};

/**
 * Manages and exposes the state data for the metrics used by the revenue wizard, as well as utility functions
 * @param metricData - API result from the useGetUtsvOperationalMetricsForPropertyYear query
 */
export default function useRevenueWizard(metricData: utsvOpMetricQueryReturn, property: Property, budgetYear: number): IUseRevenueWizardState {
    // used with the round method from math.ts to fix floating point errors
    const precision = 5;

    const [parsedMetricData, setParsedMetricData] = useState<IUTSVVersion[]>([]);

    const [rfcstExpirations, setRfcstExpirations] = useState<number[] | null>(null);
    const [bdgtExpirations, setBdgtExpirations] = useState<number[] | null>(null);
    // Corrected expirations are what we get back from the calculateExpirationCurveExpirations API
    // it adjusts our sent values to fit as best as it can, and returns an array of whole unit counts
    // the Map uses unitType IDs as keys, mapped to their corrected expiration count arrays
    // we don't just overwrite bdgtExpirations because the bdgt expiration chart needs to continue showing the users "target" expiration curve
    const [correctedBdgtExpirations, setCorrectedBdgtExpirations] = useState<Map<string, number[]>>(new Map());
    const [occupancyRate, setOccupancyRate] = useState<number>(DEFAULTS.occupancyRate);
    const [renewalRate, setRenewalRate] = useState<number>(DEFAULTS.renewalRate);
    const [forceRounding, setForceRounding] = useState(false);
    const [selectedMetricToFavor, setSelectedMetricToFavor] = useState<TMetricFavorable>("renewals");
    // Clean state is the users existing expiration curve values, as well as defaults for renewals, occupancy, and rounding
    const [cleanFormDataState, setCleanFormDataState] = useState<string>();

    // Calculates and sets expiration curves with values from the database, and also sets starting form values for dirty checks
    function initialize() {
        const getExpirationPercents = (versionType: VersionType): number[] => {
            const units = metricData.find(x => x.version == versionType)?.unitTypes;
            const expirationCounts = new Array(12).fill(0);
            let totalUnitCount = 0;

            if (units) {
                for (const unit of units) {
                    totalUnitCount += unit.unitTypeCount;
                    unit.leaseExpirationCount.map((expirations, monthIndex) => expirationCounts[monthIndex] += expirations);
                }
            }

            if (totalUnitCount <= 0) {
                return expirationCounts;
            }

            return expirationCounts.map(expirations => round((expirations / totalUnitCount) * 100, 1));
        };

        const rfcst = getExpirationPercents(VersionType.Reforecast);
        const bdgt = getExpirationPercents(VersionType.Budget);

        setRfcstExpirations(rfcst);
        setBdgtExpirations(bdgt);

        const cleanState = JSON.stringify({
            renewals: DEFAULTS.renewalRate,
            occupancy: DEFAULTS.occupancyRate,
            rfcstExpirations: rfcst,
            bdgtExpirations: bdgt,
            forceRounding: DEFAULTS.forceRounding,
            parsedMetricData: DEFAULTS.parsedMetricData,
        });
        setCleanFormDataState(cleanState);
    }

    /**
     * Reset all of the wizard's inputs to their default values.
     */
    function resetData() {
        setRfcstExpirations(DEFAULTS.rfcstExpirations);
        setBdgtExpirations(DEFAULTS.bdgtExpirations);
        setCorrectedBdgtExpirations(DEFAULTS.correctedBdgtExpirations);
        setOccupancyRate(DEFAULTS.occupancyRate);
        setRenewalRate(DEFAULTS.renewalRate);
        setForceRounding(DEFAULTS.forceRounding);
        setParsedMetricData(DEFAULTS.parsedMetricData);
    }

    // Used to determine whether the user has made any changes after they opened the wizard
    // it compares serialized versions of the initial input states and the current input states
    const isFormDirty = useMemo(() => {
        const currentState = JSON.stringify({
            renewals: renewalRate,
            occupancy: occupancyRate,
            rfcstExpirations,
            bdgtExpirations,
            forceRounding,
        });
        return currentState != cleanFormDataState;
    }, [cleanFormDataState, rfcstExpirations, bdgtExpirations, occupancyRate, renewalRate, forceRounding]);

    /**
     * Returns a value after applying the users rounding preferences
     */
    function getValueWithRounding(value: number): number {
        const preciseVal = round(value, precision);
        if (!forceRounding) {
            return preciseVal;
        }
        return Math.round(preciseVal);
    }

    function countToRate(count: number, total: number): number {
        if (total == 0) {
            return 0;
        }
        const result = (count / total) * 100;
        return round(result, precision);
    }

    /**
     * Takes a rate of format 88.7, not .887, and gives you back the unit count represented by it
     */
    function rateToCount(rate: number, total: number): number {
        const result = (rate / 100) * total;
        return round(result, precision);
    }

    function getMoveIns(occupancyCount: number, prevOccupancyCount: number, moveOutCount: number): number {
        const result = occupancyCount - (prevOccupancyCount - moveOutCount);
        return round(result, precision);
    }

    function getPreviousOccupancy(unitType: IUnitType, versionType: VersionType, monthIndex: number): number {
        if (versionType == VersionType.Reforecast) {
            return monthIndex == 0 ? unitType.actualsOccupancyCount : unitType.rfcst.occupancyCount[monthIndex - 1] ?? 0;
        }

        if (versionType == VersionType.Budget) {
            return monthIndex == 0 ? unitType.rfcst.occupancyCount[11] ?? 0 : unitType.bdgt.occupancyCount[monthIndex - 1] ?? 0;
        }

        return 0;
    }

    useEffect(() => {
        const parsedData = parseUTSVQueryResponse(metricData);
        setParsedMetricData(parsedData);
    }, [metricData]);

    // This is the base, built unit type metric data generated from recalculating current data using user inputs
    // the data is not corrected at this point so we have something to compare with final constrained and corrected values
    // generated from the convertValuesWithFavor method
    const unitTypeData = useMemo<IUnitType[]>(() => {
        // Parsing data up front gets us values as numbers, nulls as 0, and rates * 100
        const actualsData = parsedMetricData.find(x => x.version == VersionType.Actuals && x.year == budgetYear - 2);
        const rfcstData = parsedMetricData.find(x => x.version == VersionType.Reforecast);
        const bdgtData = parsedMetricData.find(x => x.version == VersionType.Budget);
        const moveOutRate = 100 - renewalRate;
        const unitTypes: IUnitType[] = [];

        if (!rfcstData || !bdgtData || !rfcstExpirations) {
            return unitTypes;
        }

        for (const unitType of rfcstData.unitTypes) {
            const bdgtExpirations = correctedBdgtExpirations.get(unitType.unitTypeId) ?? new Array(12).fill(0);
            const unitTypeActualsOccupancyRate = actualsData?.unitTypes.find(u => u.unitTypeName == unitType.unitTypeName)?.occupancyRate[11] ?? 0;
            const unitTypeActualsOccupancyCount = rateToCount(unitTypeActualsOccupancyRate, unitType.unitTypeCount);

            const ut: IUnitType = {
                name: unitType.unitTypeName,
                unitCount: unitType.unitTypeCount,
                actualsOccupancyCount: unitTypeActualsOccupancyCount,
                rfcst: {
                    // Defaulting values to what's in the db so we can render actuals before rfcst start month, but we'll overwrite the rfcst months after this
                    expirationCount: unitType.leaseExpirationCount,
                    occupancyCount: unitType.occupancyRate.map(occupancy => rateToCount(occupancy, unitType.unitTypeCount)),
                    renewalCount: unitType.renewalRatio.map((renewals, monthIndex) => rateToCount(renewals, unitType.leaseExpirationCount[monthIndex] ?? 0)),
                    moveOutCount: unitType.moveOutRate.map((moveOuts, monthIndex) => rateToCount(moveOuts, unitType.leaseExpirationCount[monthIndex] ?? 0)),
                    moveInCount: new Array(12).fill(0),
                },
                bdgt: {
                    expirationCount: bdgtExpirations,
                    occupancyCount: new Array(12).fill(getValueWithRounding(rateToCount(occupancyRate, unitType.unitTypeCount))),
                    renewalCount: bdgtExpirations.map(x => getValueWithRounding(rateToCount(renewalRate, x))),
                    moveOutCount: bdgtExpirations.map(x => getValueWithRounding(rateToCount(moveOutRate, x))),
                    moveInCount: new Array(12).fill(0),
                },
            };

            // Replace rfcst metric values with our new inputs from the wizard and calculate the moveInCounts for rfcst period
            for (let monthIndex = property.reforecastStartMonthIndex; monthIndex < 12; monthIndex++) {
                const expirationRate = rfcstExpirations[monthIndex];
                // expirations have to be whole numbers, so we enforce it here with regular rounding
                const expirationCount = Math.round(rateToCount(expirationRate ?? 0, unitType.unitTypeCount));

                ut.rfcst.expirationCount[monthIndex] = expirationCount;
                ut.rfcst.occupancyCount[monthIndex] = getValueWithRounding(rateToCount(occupancyRate, unitType.unitTypeCount));
                ut.rfcst.renewalCount[monthIndex] = getValueWithRounding(rateToCount(renewalRate, expirationCount));
                ut.rfcst.moveOutCount[monthIndex] = getValueWithRounding(rateToCount(moveOutRate, expirationCount));

                // Calculate moveInCounts for rfcst period
                const rfcstOccupancy = ut.rfcst.occupancyCount[monthIndex] ?? 0;
                const rfcstMoveOuts = ut.rfcst.moveOutCount[monthIndex] ?? 0;
                const prevOccupancy = getPreviousOccupancy(ut, VersionType.Reforecast, monthIndex);

                ut.rfcst.moveInCount[monthIndex] = getMoveIns(rfcstOccupancy, prevOccupancy, rfcstMoveOuts);
            }

            // Calculate moveInCounts for bdgt period
            for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
                const prevBdgtOccupancy = getPreviousOccupancy(ut, VersionType.Budget, monthIndex);
                const bdgtOccupancy = ut.bdgt.occupancyCount[monthIndex] ?? 0;
                const bdgtMoveOuts = ut.bdgt.moveOutCount[monthIndex] ?? 0;
                ut.bdgt.moveInCount[monthIndex] = getMoveIns(bdgtOccupancy, prevBdgtOccupancy, bdgtMoveOuts);
            }

            unitTypes.push(ut);
        }

        return unitTypes;
    }, [parsedMetricData, rfcstExpirations, correctedBdgtExpirations, occupancyRate, renewalRate, forceRounding]);

    /**
     * Finds the occupancyRate, renewalRate, and moveInCount that all most
     * closely align with the users target values without breaking constraints, favoring sticking to Renewals over Occupancy.
     * Favoring basically means "locking" that metric so we can control the other variables to fit it
     * We can always raise occupancy to 100% to fit any renewal rate, so this one is less involved
     */
    function getCorrectedValuesFavorRenewals(unitType: IUnitType, monthIndex: number, versionType: VersionType): {renewalRate: number, occupancyRate: number, moveInCount: number} {
        const targetMoveIns = 0;

        const utv = versionType == VersionType.Reforecast ? unitType.rfcst : unitType.bdgt;

        const expirations = utv.expirationCount[monthIndex] ?? 0;
        const occupancy = utv.occupancyCount[monthIndex] ?? 0;
        const renewals = utv.renewalCount[monthIndex] ?? 0;
        const prevOccupancy = getPreviousOccupancy(unitType, versionType, monthIndex);

        const moveIns = getMoveIns(
            occupancy,
            prevOccupancy,
            expirations - renewals,
        );

        // default to current values
        const adjustedRates = {
            renewalRate: countToRate(renewals, expirations),
            occupancyRate: countToRate(occupancy, unitType.unitCount),
            moveInCount: moveIns,
        };

        // if current values give negative move ins, recalculate what occupancy would need to be if renewals stayed the same
        if (moveIns < 0) {
            const occupancyNeeded = round(targetMoveIns + prevOccupancy - (expirations - renewals), precision);
            const finalOccupancyCount = getValueWithRounding(occupancyNeeded);

            const finalRenewalCount = getValueWithRounding(rateToCount(renewalRate, expirations));
            const finalMoveOutCount = round(expirations - finalRenewalCount, precision);
            const finalMoveIns = round(finalOccupancyCount - (prevOccupancy - finalMoveOutCount), precision);

            adjustedRates.moveInCount = round(finalMoveIns, precision);
            adjustedRates.occupancyRate = countToRate(finalOccupancyCount, unitType.unitCount);
        }

        // if no expirations, then renewal rate doesn't matter so just set it to the users target
        if (expirations <= 0) {
            adjustedRates.renewalRate = renewalRate;
        }

        return adjustedRates;
    }

    /**
     * Finds the occupancyRate, renewalRate, and moveInCount that all most
     * closely align with the users target values without breaking constraints, favoring sticking to Occupancy over Renewals.
     * Favoring basically means "locking" that metric so we can control the other variables to fit it
     */
    function getCorrectedValuesFavorOccupancy(unitType: IUnitType, monthIndex: number, versionType: VersionType): {renewalRate: number, occupancyRate: number, moveInCount: number} {
        const targetMoveIns = 0;

        const utv = versionType == VersionType.Reforecast ? unitType.rfcst : unitType.bdgt;

        const expirations = utv.expirationCount[monthIndex] ?? 0;
        const occupancy = utv.occupancyCount[monthIndex] ?? 0;
        const renewals = utv.renewalCount[monthIndex] ?? 0;
        const prevOccupancy = getPreviousOccupancy(unitType, versionType, monthIndex);

        const moveIns = getMoveIns(
            occupancy,
            prevOccupancy,
            expirations - renewals,
        );

        // base values to use for our corrected values
        const adjustedRates = {
            renewalRate: countToRate(renewals, expirations),
            occupancyRate: countToRate(occupancy, unitType.unitCount),
            moveInCount: moveIns,
        };

        if (moveIns < 0) {
            const targetOccupancyCount = getValueWithRounding(rateToCount(occupancyRate, unitType.unitCount));
            const neededMoveOuts = targetMoveIns - targetOccupancyCount + prevOccupancy;
            const neededRenewals = expirations - neededMoveOuts;

            // if the needed renewals to hit our occupancy target are less than 0 or more than our total expirations
            // then we cannot give the user their target occupancy, and instead solve as close as we can for 0 move ins
            if (neededRenewals < 0 || neededRenewals > expirations) {
                const minOccupancyCount = targetMoveIns + prevOccupancy - expirations;
                adjustedRates.occupancyRate = countToRate(minOccupancyCount, unitType.unitCount);
                adjustedRates.renewalRate = 0;
            } else {
                // we have the expirations to satisfy user targets
                adjustedRates.occupancyRate = countToRate(targetOccupancyCount, unitType.unitCount);
                adjustedRates.renewalRate = countToRate(neededRenewals, expirations);
            }

            // use our adjusted values to calculate new move in count
            const finalOccupancyCount = rateToCount(adjustedRates.occupancyRate, unitType.unitCount);
            const finalRenewalCount = rateToCount(adjustedRates.renewalRate, expirations);
            const finalMoveIns = finalOccupancyCount - (prevOccupancy - (expirations - finalRenewalCount));

            adjustedRates.moveInCount = round(finalMoveIns, precision);
        }

        // if no expirations, then renewal rate doesn't matter so just set it to the users target
        if (expirations <= 0) {
            adjustedRates.renewalRate = renewalRate;
        }

        return adjustedRates;
    }

    /**
     * Takes an array of unit type data and converts every value for every unit type for every month from the rfcst start month forward into corrected values
     * that match as closely as possible to the user's target values while honoring a constraint of "no negative move ins".
     *
     * IMPORTANT - This ONLY works when we solve for corrected values moving forward in time. You can't jump in the middle and solve backwards
     * without doing wildly more complicated math.
     */
    function convertValuesWithFavor(unitTypes: IUnitType[], metric: TMetricFavorable): IUnitType[] {
        // clone unit types without references
        const adjustedUnitTypes: IUnitType[] = JSON.parse(JSON.stringify(unitTypes));

        function buildUnitVersionData(
            unitType: IUnitType,
            monthIndex: number,
            versionType: VersionType,
        ) {
            let correctedValuesForUnitType = getCorrectedValuesFavorRenewals(unitType, monthIndex, versionType);

            if (metric == "occupancy") {
                correctedValuesForUnitType = getCorrectedValuesFavorOccupancy(unitType, monthIndex, versionType);
            }

            const correctedRenewalRate = correctedValuesForUnitType.renewalRate;
            const correctedMoveOutRate = 100 - correctedValuesForUnitType.renewalRate;
            const correctedOccupancyRate = correctedValuesForUnitType.occupancyRate;

            const utv = versionType == VersionType.Reforecast ? unitType.rfcst : unitType.bdgt;
            const expirationCount = utv.expirationCount[monthIndex] ?? 0;

            utv.renewalCount[monthIndex] = getValueWithRounding(rateToCount(correctedRenewalRate, expirationCount));
            utv.moveOutCount[monthIndex] = getValueWithRounding(rateToCount(correctedMoveOutRate, expirationCount));
            utv.occupancyCount[monthIndex] = getValueWithRounding(rateToCount(correctedOccupancyRate, unitType.unitCount));

            const prevOccupancy = getPreviousOccupancy(unitType, versionType, monthIndex);

            utv.moveInCount[monthIndex] = getMoveIns(
                utv.occupancyCount[monthIndex] ?? 0,
                prevOccupancy,
                utv.moveOutCount[monthIndex] ?? 0,
            );
        }

        for (const unitType of adjustedUnitTypes) {
            for (let monthIndex = property.reforecastStartMonthIndex; monthIndex < 12; monthIndex++) {
                buildUnitVersionData(
                    unitType,
                    monthIndex,
                    VersionType.Reforecast,
                );
            }

            for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
                buildUnitVersionData(
                    unitType,
                    monthIndex,
                    VersionType.Budget,
                );
            }
        }

        return adjustedUnitTypes;
    }

    // These values are for display purposes only, but they provide version summary values
    // for the preview cards and summary table
    function getUnitTypeVersionSummary(unitTypes: IUnitType[]): IUnitType {
        const summaryData: IUnitType = {
            name: "All Unit Types",
            unitCount: 0,
            actualsOccupancyCount: 0,
            rfcst: {
                expirationCount: new Array(12).fill(0),
                occupancyCount: new Array(12).fill(0),
                renewalCount: new Array(12).fill(0),
                moveOutCount: new Array(12).fill(0),
                moveInCount: new Array(12).fill(0),
            },
            bdgt: {
                expirationCount: new Array(12).fill(0),
                occupancyCount: new Array(12).fill(0),
                renewalCount: new Array(12).fill(0),
                moveOutCount: new Array(12).fill(0),
                moveInCount: new Array(12).fill(0),
            }
        };

        const metricKeys = Object.keys(summaryData.rfcst) as (keyof IUnitTypeMetrics)[];

        for (const unitType of unitTypes) {
            summaryData.unitCount += unitType.unitCount;
            summaryData.actualsOccupancyCount += unitType.actualsOccupancyCount;

            for (const metricKey of metricKeys) {
                summaryData.rfcst[metricKey] = summaryData.rfcst[metricKey].map((x, i) => round(x + (unitType.rfcst[metricKey][i] ?? 0), precision));
                summaryData.bdgt[metricKey] = summaryData.bdgt[metricKey].map((x, i) => round(x + (unitType.bdgt[metricKey][i] ?? 0), precision));
            }
        }

        return summaryData;
    }

    return {
        property,
        budgetYear,
        unitTypeData,
        rfcstExpirations,
        bdgtExpirations,
        correctedBdgtExpirations,
        occupancyRate,
        renewalRate,
        forceRounding,
        isFormDirty,
        selectedMetricToFavor,
        setSelectedMetricToFavor,
        setRfcstExpirations,
        setBdgtExpirations,
        setCorrectedBdgtExpirations,
        setOccupancyRate,
        setRenewalRate,
        setForceRounding,
        getValueWithRounding,
        countToRate,
        rateToCount,
        initialize,
        resetData,
        getCorrectedValuesFavorRenewals,
        getCorrectedValuesFavorOccupancy,
        convertValuesWithFavor,
        getUnitTypeVersionSummary,
        getPreviousOccupancy,
    };
}
